import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import CustomDropdown from "../../components/CustomDropdown";
import DatePicker from "../../components/DatePicker";
import Dropdown from "../../components/Dropdown";
import { FormCard } from "../FormCard";
import { buildOpeningHoursSummary } from "./locationCardSummaries";

const weekdays = [
  { id: 1, key: "MONDAY" },
  { id: 2, key: "TUESDAY" },
  { id: 3, key: "WEDNESDAY" },
  { id: 4, key: "THURSDAY" },
  { id: 5, key: "FRIDAY" },
  { id: 6, key: "SATURDAY" },
  { id: 7, key: "SUNDAY" },
];

export default function HoursEditor({
  openingTimes,
  setOpeningTimes,
  setHasChanges,
}) {
  const { t } = useTranslation();
  const addRegularHour = () => {
    setHasChanges(true);
    setOpeningTimes((prev) => ({
      ...prev,
      regular_hours: [
        ...prev.regular_hours,
        { weekday: 1, period_begin: "08:00", period_end: "20:00" },
      ],
    }));
  };

  const updateRegularHour = (index, key, value) => {
    setHasChanges(true);
    setOpeningTimes((prev) => {
      const updated = [...prev.regular_hours];
      updated[index][key] = value;
      return { ...prev, regular_hours: updated };
    });
  };

  const removeRegularHour = (index) => {
    setHasChanges(true);
    setOpeningTimes((prev) => {
      const updated = [...prev.regular_hours];
      updated.splice(index, 1);
      return { ...prev, regular_hours: updated };
    });
  };

  const addExceptional = (type) => {
    setHasChanges(true);
    const today = new Date().toISOString().split("T")[0];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    setOpeningTimes((prev) => ({
      ...prev,
      [type]: [
        ...prev[type],
        {
          period_begin: `${today}T00:00:00Z`,
          period_end: `${tomorrow.toISOString().split("T")[0]}T00:00:00Z`,
        },
      ],
    }));
  };

  const updateExceptional = (type, index, key, value, date = false) => {
    setHasChanges(true);
    setOpeningTimes((prev) => {
      const updated = [...prev[type]];
      if (date) {
        const time = updated[index][key].split("T")[1];
        const dateValue = value.split("T")[0];
        updated[index][key] = dateValue + "T" + time;
      } else {
        const dateValue = updated[index][key].split("T")[0];
        updated[index][key] = dateValue + "T" + value;
      }
      return { ...prev, [type]: updated };
    });
  };

  const removeExceptional = (type, index) => {
    setHasChanges(true);
    setOpeningTimes((prev) => {
      const updated = [...prev[type]];
      updated.splice(index, 1);
      return { ...prev, [type]: updated };
    });
  };

  if (!openingTimes) {
    return null;
  }

  return (
    <FormCard
      title={t("site.location.hoursTitle")}
      description={t("site.location.hoursDescription")}
      summary={buildOpeningHoursSummary(openingTimes, t)}
      collapsible
      defaultOpen={false}
    >
      <div className="flex flex-col gap-8">
        <div>
          <h4 className="text-base font-semibold mb-4">{t("site.location.regularHours")}</h4>
          <div className="flex flex-col items-left">
            {openingTimes.regular_hours.map((hour, index) => (
              <div
                key={index}
                className="flex space-x-2 items-center mb-2 gap-4 mt-2"
              >
                <Dropdown
                  className="pb-2"
                  options={weekdays.map((d) => ({
                    value: d.id,
                    label: t(`weekday.${d.key}`),
                  }))}
                  selectedOptions={[hour.weekday]}
                  onSelect={(selectedId) => {
                    if (selectedId != null) {
                      updateRegularHour(index, "weekday", selectedId);
                    }
                  }}
                />
                <input
                  type="time"
                  value={hour.period_begin}
                  onChange={(e) =>
                    updateRegularHour(index, "period_begin", e.target.value)
                  }
                  className="w-full input-default"
                />
                <input
                  type="time"
                  value={hour.period_end}
                  onChange={(e) =>
                    updateRegularHour(index, "period_end", e.target.value)
                  }
                  className="w-full input-default"
                />
                <button onClick={() => removeRegularHour(index)}>
                  <FontAwesomeIcon icon={faTrash} className="w-4 text-error-400" />
                </button>
              </div>
            ))}
            <div>
              <button className="btn btn-primary mt-2" onClick={addRegularHour}>
                {t("common.addPlus")}
              </button>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-base font-semibold mb-4">{t("site.location.exceptionalOpenings")}</h4>
          <div className="flex flex-col items-left">
            {openingTimes.exceptional_openings.map((exception, index) => (
              <div
                key={index}
                className="flex space-x-2 items-center mb-2 gap-4 mt-2"
              >
                <CustomDropdown
                  isRight={false}
                  ContentComponent={() => (
                    <DatePicker
                      startDate={exception.period_begin}
                      endDate={exception.period_end}
                      setStartDate={(e) =>
                        e &&
                        updateExceptional(
                          "exceptional_openings",
                          index,
                          "period_begin",
                          e.toISOString(),
                          true
                        )
                      }
                      setEndDate={(e) =>
                        e &&
                        updateExceptional(
                          "exceptional_openings",
                          index,
                          "period_end",
                          e.toISOString(),
                          true
                        )
                      }
                    />
                  )}
                >
                  <div className="flex gap-2 w-[12rem] justify-between items-center">
                    <button
                      className="text-sm font-normal text-black w-full flex justify-between items-center"
                      onClick={() => {}}
                    >
                      {`${exception.period_begin.split("T")[0]} - ${
                        exception.period_end.split("T")[0]
                      }`}
                      <FontAwesomeIcon icon={faChevronDown} />
                    </button>
                  </div>
                </CustomDropdown>
                <input
                  type="time"
                  value={exception.period_begin.split("T")[1].replace("Z", "")}
                  onChange={(e) =>
                    updateExceptional(
                      "exceptional_openings",
                      index,
                      "period_begin",
                      e.target.value + "Z",
                      false
                    )
                  }
                  className="w-full input-default"
                />
                <input
                  type="time"
                  value={exception.period_end.split("T")[1].replace("Z", "")}
                  onChange={(e) =>
                    updateExceptional(
                      "exceptional_openings",
                      index,
                      "period_end",
                      e.target.value + "Z"
                    )
                  }
                  className="w-full input-default"
                />
                <button
                  onClick={() =>
                    removeExceptional("exceptional_openings", index)
                  }
                >
                  <FontAwesomeIcon icon={faTrash} className="w-4 text-error-400" />
                </button>
              </div>
            ))}
            <div>
              <button
                className="btn btn-primary mt-2"
                onClick={() => addExceptional("exceptional_openings")}
              >
                {t("common.addPlus")}
              </button>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-base font-semibold mb-4">{t("site.location.exceptionalClosings")}</h4>
          <div className="flex flex-col items-left">
            {openingTimes.exceptional_closings.map((exception, index) => (
              <div
                key={index}
                className="flex space-x-2 items-center mb-2 gap-4 mt-2"
              >
                <CustomDropdown
                  isRight={false}
                  ContentComponent={() => (
                    <DatePicker
                      startDate={exception.period_begin}
                      endDate={exception.period_end}
                      setStartDate={(e) =>
                        e &&
                        updateExceptional(
                          "exceptional_closings",
                          index,
                          "period_begin",
                          e.toISOString(),
                          true
                        )
                      }
                      setEndDate={(e) =>
                        e &&
                        updateExceptional(
                          "exceptional_closings",
                          index,
                          "period_end",
                          e.toISOString(),
                          true
                        )
                      }
                    />
                  )}
                >
                  <div className="flex gap-2 w-[12rem] justify-between items-center">
                    <button
                      className="text-sm font-normal text-black w-full flex justify-between items-center"
                      onClick={() => {}}
                    >
                      {`${exception.period_begin.split("T")[0]} - ${
                        exception.period_end.split("T")[0]
                      }`}
                      <FontAwesomeIcon icon={faChevronDown} />
                    </button>
                  </div>
                </CustomDropdown>
                <input
                  type="time"
                  value={exception.period_begin.split("T")[1].replace("Z", "")}
                  onChange={(e) =>
                    updateExceptional(
                      "exceptional_closings",
                      index,
                      "period_begin",
                      e.target.value + "Z",
                      false
                    )
                  }
                  className="w-full input-default"
                />
                <input
                  type="time"
                  value={exception.period_end.split("T")[1].replace("Z", "")}
                  onChange={(e) =>
                    updateExceptional(
                      "exceptional_closings",
                      index,
                      "period_end",
                      e.target.value + "Z"
                    )
                  }
                  className="w-full input-default"
                />
                <button
                  onClick={() =>
                    removeExceptional("exceptional_closings", index)
                  }
                >
                  <FontAwesomeIcon icon={faTrash} className="w-4 text-error-400" />
                </button>
              </div>
            ))}
            <div>
              <button
                className="btn btn-primary mt-2"
                onClick={() => addExceptional("exceptional_closings")}
              >
                {t("common.addPlus")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </FormCard>
  );
}
